import FileDropZone from "@components/fileDropZone";
import TipDot from "@components/tipDot";
import { IInboxItem } from "@data/idb";
import BlobStore from "@data/idb/blobStore";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonFooter,
  IonHeader,
  IonLabel,
  IonNote,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from "@ionic/react";
import MapView from "@lib/mapView";
import { IDeck } from "@models/deck";
import { Inbox } from "@models/inbox";
import { IKnol, Knol } from "@models/knol";
import { User } from "@models/user";
import logEvent from "analytics";
import { blobHash } from "blobs/lib";
import { clearDot } from "dots";
import { useFlagAndContext } from "featureFlags";
import { IField, fieldTypeMap } from "fields/fields";
import useKeyboardHeight from "hooks/useKeyboardHeight";
import useConfirmCancellationDialog from "hooks/util/useConfirmCancellationDialog";
import useErrorAlert from "hooks/util/useErrorAlert";
import { file2Base64 } from "lib";
import Lib from "lib";
import L10n from "localization";
import Network from "network";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  LuAlertTriangle,
  LuBookOpen,
  LuGraduationCap,
  LuLightbulb,
  LuSparkles,
  LuWand2,
} from "react-icons/lu";
import { ID } from "types/ID";
import FieldsCard from "./genCards/fieldsCard";
import GenResults from "./genCards/genResults";
import {
  IGenerateResponse,
  IGeneratedRow,
  isLikelyDuplicate,
  objDiff,
  parseGeneratedResponse,
} from "./genCards/lib";
import LoadingTable from "./genCards/loadingTable";
import PromptCard from "./genCards/promptCard";
import PromptInput from "./genCards/promptInput";

export const genSourceImageFieldName = "$genSrcImg$";

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export async function genCards({
  prompt,
  files,
  deck,
  sessionID,
  pending,
  numKnols,
}: {
  deck: IDeck;
  prompt?: string | null;
  files?: File[];
  sessionID: ID | null | undefined;
  pending: Record<string, string>[];
  numKnols: number;
}): Promise<{
  resp: IGenerateResponse;
  headers: Headers;
}> {
  let filesBase64: string[] | undefined;
  if (files?.length) {
    filesBase64 = await Promise.all(
      files.map(async (f) => {
        const blob = new Blob([f], { type: f.type });
        return file2Base64(blob);
      }),
    );
  }
  const [resp, { headers }] = await Network.fetchWithMetadata<IGenerateResponse>({
    action: "POST",
    path: "/decks/ai",
    data: {
      files: filesBase64,
      prompt,
      deck: {
        ...deck,
        num_knols: numKnols,
      },
      pending,
      session_id: sessionID,
      sys_lang: navigator.language.toLocaleLowerCase(),
    },
  });
  return { resp, headers };
}

// Icon mapping for different types of prompts
const getPromptIcon = (prompt: string): { icon: JSX.Element; background: string } => {
  const lowerPrompt = prompt.toLowerCase();

  if (
    lowerPrompt.includes("learn") ||
    lowerPrompt.includes("study") ||
    lowerPrompt.includes("education")
  ) {
    return {
      icon: <LuGraduationCap size={16} color="#3880ff" />,
      background: "#e8f2ff",
    };
  }
  if (
    lowerPrompt.includes("vocabulary") ||
    lowerPrompt.includes("language") ||
    lowerPrompt.includes("word")
  ) {
    return {
      icon: <LuBookOpen size={16} color="#3dc2ff" />,
      background: "#e8f8ff",
    };
  }
  if (
    lowerPrompt.includes("generate") ||
    lowerPrompt.includes("create") ||
    lowerPrompt.includes("make")
  ) {
    return {
      icon: <LuWand2 size={16} color="#5260ff" />,
      background: "#eff0ff",
    };
  }
  if (
    lowerPrompt.includes("practice") ||
    lowerPrompt.includes("exercise") ||
    lowerPrompt.includes("drill")
  ) {
    return {
      icon: <LuSparkles size={16} color="#2dd36f" />,
      background: "#e8f9f0",
    };
  }

  // Default icon
  return {
    icon: <LuLightbulb size={16} color="#92949c" />,
    background: "#f4f5f8",
  };
};

export async function fetchSuggestedPrompts(deck: IDeck): Promise<string[]> {
  try {
    const requestData: ISuggestedPromptsRequest = {
      deck_name: deck.name,
    };

    // Add deck configuration if available
    if (deck.config?.fields) {
      requestData.deck_config = {
        fields: deck.config.fields.map((field) => ({
          name: field.name,
          type: field.type,
          attributes:
            field.type === "tts" && field.attributes?.lang
              ? { lang: field.attributes.lang }
              : undefined,
        })),
      };
    }

    const response = await Network.fetch<ISuggestedPromptsResponse>(
      "POST",
      "/decks/suggested-prompts",
      requestData,
    );

    return response.prompts || [];
  } catch (error) {
    console.warn("Failed to fetch suggested prompts:", error);
    // Return empty array on error to gracefully degrade
    return [];
  }
}

interface IGeneration {
  prompt: string | null | undefined;
  files: File[] | undefined;
  rows: IGeneratedRow[];
}

interface ISuggestedPromptsRequest {
  deck_name: string;
  deck_config?: {
    fields: Array<{
      name: string;
      type: string;
      attributes?: {
        lang?: string;
      };
    }>;
  };
}

interface ISuggestedPromptsResponse {
  prompts: string[];
}

interface IProps {
  deck: IDeck;
  knols: MapView<ID, IKnol>;
  dismiss: () => void;
  setCanDismiss: (v: boolean) => void;
}

export default function GenCardsModal(props: IProps): JSX.Element {
  const { deck, dismiss, knols, setCanDismiss } = props;

  const [presentAlert] = useIonAlert();

  useEffect(() => {
    clearDot("generate_cards_menu_item").catch(() => {});
  }, []);

  const [inboxItem, setInboxItem] = useState<IInboxItem>();
  useEffect(() => {
    async function loadQueuedBlob() {
      try {
        const item = await Inbox.getItemQueuedForGeneration();
        if (!item) {
          return;
        }
        setInboxItem(item);
        // item.blob can be any Blob (likely a File or a plain Blob), but FileDropZone/attachments always want File[]
        // So, create a File object from the Blob, using its type if available (item.blob.type)
        const file = new File([item.blob], `inbox_item_${Date.now()}`, {
          type: item.blob.type ?? "application/octet-stream",
        });
        setFiles((prev) => (prev ? prev.concat(file) : [file]));
        Inbox.clearItemQueuedForGeneration();
      } catch (err) {
        console.log(err);
      }
    }
    loadQueuedBlob();
  }, []);

  const [prompt, setPrompt] = useState<string | null | undefined>();
  const [files, setFiles] = useState<File[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generations, setGenerations] = useState<IGeneration[]>([]);
  const [saving, setSaving] = useState(false);
  const [generated, setGenerated] = useState(false);

  const numCharsPerToken = 4;
  const features = useFlagAndContext("features");
  const aiFlag = features?.flag?.ai_card_create;
  const maxTokens = aiFlag?.max_tokens ?? 1024;
  const maxChars = maxTokens * numCharsPerToken;

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Any fields where have a ref as their source should not have content generated,
  // since they derive their content directly from another field.
  const refFields = new Map<string, IField>();
  for (const field of deck.config?.fields ?? []) {
    refFields.set(field.name, field);
  }
  const nonRefFields = deck.config?.fields.filter((f) => f.source?.type !== "ref") ?? [];
  const columns = deck.config?.fields.map((f) => f.name) ?? [];
  const nonRefColumns = nonRefFields.map((f) => f.name);

  const disabled =
    aiFlag === undefined || saving || generating || deck.config === undefined || generated;

  const sessionID = useRef<ID | null>();

  const dirty = generations.length > 0;
  useEffect(() => {
    setCanDismiss(!dirty);
  }, [dirty, setCanDismiss]);
  const presentConfirmCancel = useConfirmCancellationDialog(dismiss);
  const handleClose = useCallback(() => {
    if (!dirty) {
      dismiss();
      return;
    }
    presentConfirmCancel();
  }, [dirty, dismiss, presentConfirmCancel]);

  const [showError] = useErrorAlert({ code: "GENERATING_CARDS" });
  const generate = useCallback(async () => {
    // Prevent generation if prompt is empty or only whitespace and no files
    if ((!prompt || prompt.trim() === "") && (!files || files.length === 0)) {
      return;
    }

    try {
      setGenerating(true);

      // List of knols generated previously in this session.
      const pending = generations.flatMap((gen) =>
        gen.rows.filter((row) => row.enabled).map((row) => row.values),
      );

      // HACK: I had to do this to prefix the `type/` onto the front of the MIME type.
      const { resp, headers } = await genCards({
        prompt,
        files,
        deck,
        sessionID: sessionID.current,
        pending,
        numKnols: knols.size,
      });
      sessionID.current = headers.get("x-ankiapp-ai-session-id");
      const respRows = parseGeneratedResponse(resp, columns, knols);

      // Sort duplicates to end.
      const nonDupeRows = respRows.filter((row) => !row.duplicate);
      const dupeRows = respRows.filter((row) => row.duplicate);
      const sortedRows = nonDupeRows.concat(dupeRows);

      const newGen = { prompt, rows: sortedRows, files };
      const newGens = generations.concat(newGen);
      setGenerations(newGens);
      setFiles([]);
      setPrompt(undefined);
      setGenerated(true);

      logEvent("knol_generate_event", {
        step: "generate",
        sessionID: sessionID.current,
        hasFile: files?.length && files.length > 0,
        prompt,
        numTotal: sortedRows.length,
      });
    } catch (err) {
      showError(err);
    } finally {
      setGenerating(false);
    }
  }, [prompt, generations, deck, columns, knols, showError, files]);

  // Patches override the value of a row, identified by row number.
  const [patches, setPatches] = useState<Map<number, Map<number, IGeneratedRow>>>(new Map());
  const updateRow = useCallback(
    (genIndex: number, rowIndex: number, newRow: IGeneratedRow) => {
      const duplicate = isLikelyDuplicate(knols, newRow.values);
      const newPatches = new Map(patches);
      const genMap = new Map<number, IGeneratedRow>(newPatches.get(genIndex));
      genMap.set(rowIndex, { ...newRow, duplicate });
      newPatches.set(genIndex, genMap);
      setPatches(newPatches);
    },
    [knols, patches],
  );

  // Apply patches.
  const gens: IGeneration[] = [];
  for (const gen of generations) {
    const clone: IGeneration = JSON.parse(JSON.stringify(gen)); // Ugh. Have to do this to clone rows.
    clone.files = gen.files;
    gens.push(clone);
  }
  for (const [i, genMap] of patches) {
    for (const [j, patch] of genMap) {
      if (gens?.[i]?.rows?.[j]) {
        gens[i].rows[j] = patch;
      }
    }
  }

  let numDupes = 0;
  let numEnabled = 0;
  let numTotal = 0;
  for (const gen of gens ?? []) {
    for (const row of gen.rows) {
      if (row.duplicate) {
        numDupes += 1;
      }
      if (row.enabled) {
        numEnabled += 1;
      }
      numTotal += 1;
    }
  }

  const save = useCallback(async () => {
    if (!gens) {
      return;
    }
    try {
      setSaving(true);
      const knolIDs: ID[] = [];
      const prompts: string[] = [];

      for (const gen of gens) {
        // Record the source blob.
        const genSrcBlobID = Lib.uuid16();
        if (gen.files) {
          for (const file of gen.files) {
            const sha256 = await blobHash(file);
            // TODO: do this in a single transaction.
            await BlobStore.insertBlob({
              id: genSrcBlobID,
              deckID: deck.id,
              blob: file,
              sha256,
            });
          }
        }

        let anyEnabled = false;
        for (const row of gen.rows) {
          if (!row.enabled) {
            continue;
          }
          anyEnabled = true;

          const values = row.values;
          const knolID = Lib.uuid16();

          // Add blob reference.
          if (User.isInternal()) {
            if (gen.files) {
              for (const file of gen.files) {
                values[genSourceImageFieldName] = fieldTypeMap.image.dumpFML({
                  id: genSrcBlobID,
                  type: file.type,
                });
                await BlobStore.markBlobsAsSavedAndPendingUpload([genSrcBlobID], knolID);
              }
            }
          }

          const knol: IKnol = {
            id: knolID,
            deck_id: deck.id,
            values,
          };
          await Knol.Create(deck.id, knol);
          knolIDs.push(knol.id);
        }
        if (anyEnabled && gen.prompt) {
          prompts.push(gen.prompt);
        }
      }
      dismiss();

      if (inboxItem) {
        await Inbox.markItemSaved(inboxItem, deck.id);
        logEvent("inbox_event", { step: "item_saved_to_deck" });
      }

      let numEdited = 0;
      const edits: Record<string, string> = {};
      for (const [i, genMap] of patches) {
        for (const [j, patch] of genMap) {
          const origRow = generations[i]?.rows?.[j];
          if (patch.enabled && origRow && objDiff(patch.values, origRow.values)) {
            numEdited += 1;
            for (const key in patch.values) {
              const orig = origRow.values[key];
              const edit = patch.values[key];
              if (edit !== orig) {
                edits[orig] = edit;
              }
            }
          }
        }
      }
      logEvent("knol_generate_event", {
        step: "save",
        sessionID: sessionID.current,
        prompts,
        numTotal,
        numDupes,
        numEnabled,
        numEdited,
        edits,
        knolIDs,
      });
    } catch (err) {
      // TODO: handle intermediate error (error in middle of inserting all knols).
    } finally {
      setSaving(false);
    }
  }, [
    gens,
    patches,
    generations,
    deck.id,
    dismiss,
    numDupes,
    numEnabled,
    numTotal,
    inboxItem,
    nonRefFields,
    refFields,
  ]);

  const [showPhotoPickerError] = useErrorAlert({ code: "PICKING_PHOTO" });
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (disabled) {
        return;
      }
      const validFiles: File[] = [];
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          presentAlert(
            L10n.localize((s) => s.funcs.general.fileTooLarge)(
              file.name,
              (file.size / 1024 / 1024).toFixed(2),
              MAX_FILE_SIZE_MB,
            ),
          );
          continue;
        }
        validFiles.push(file);
      }
      try {
        setFiles((prev) => (prev ? prev.concat(validFiles) : validFiles));
      } catch (err) {
        showPhotoPickerError(err);
      }
    },
    [disabled, showPhotoPickerError],
  );

  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([]);
  const [loadingSuggestedPrompts, setLoadingSuggestedPrompts] = useState(false);

  useEffect(() => {
    async function loadSuggestedPrompts() {
      if (!deck) {
        return;
      }

      setLoadingSuggestedPrompts(true);
      try {
        const prompts = await fetchSuggestedPrompts(deck);
        setSuggestedPrompts(prompts);
      } catch (error) {
        console.warn("Error loading suggested prompts:", error);
        // Fallback to empty array - UI will handle gracefully
        setSuggestedPrompts([]);
      } finally {
        setLoadingSuggestedPrompts(false);
      }
    }

    loadSuggestedPrompts();
  }, [deck]);

  const [keyboardOpen, setKeyboardShowing] = useState(false);
  const onKeyboardShow = useCallback(() => setKeyboardShowing(true), [setKeyboardShowing]);
  const onKeyboardHide = useCallback(() => setKeyboardShowing(false), [setKeyboardShowing]);
  const kbHeightPx = useKeyboardHeight(onKeyboardShow, onKeyboardHide);

  return (
    <IonPage style={{ marginBottom: keyboardOpen ? undefined : "env(safe-area-inset-bottom)" }}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{L10n.localize((s) => s.card.generate)}</IonTitle>
          <IonButtons slot="secondary">
            <IonButton onClick={handleClose} color={dirty ? "danger" : undefined}>
              {dirty
                ? L10n.localize((s) => s.actions.cancel)
                : L10n.localize((s) => s.actions.close)}
            </IonButton>
          </IonButtons>
          <IonButtons slot="primary">
            <IonButton onClick={save} disabled={saving || numEnabled < 1 || generating}>
              {saving ? <IonSpinner /> : L10n.localize((s) => s.actions.save)}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonFooter
        style={{
          display: generating ? "none" : undefined,
          textAlign: "center",
          paddingBottom: kbHeightPx,
        }}
      >
        {generated ? (
          <IonButton fill="clear" onClick={() => setGenerated(false)}>
            {L10n.localize((s) => s.generation.generateMore)}
          </IonButton>
        ) : (
          <PromptInput
            disabled={disabled}
            prompt={prompt}
            setPrompt={setPrompt}
            pickPhoto={() => fileInputRef.current?.click()}
            generate={generate}
            maxChars={maxChars}
            enableText={aiFlag?.with_prompt}
            enablePhoto={aiFlag?.with_image}
            hasFiles={files && files.length > 0}
          />
        )}
      </IonFooter>
      <IonContent color="light">
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpeg,.jpg,.gif,.webp,.pdf"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            const validFiles: File[] = [];
            const allowedExts = ".png, .jpeg, .jpg, .gif, .webp, .pdf";
            const allowedMimeTypes = [
              "image/png",
              "image/jpeg",
              "image/jpg",
              "image/gif",
              "image/webp",
              "application/pdf",
            ];
            const allowedExtensions = [".png", ".jpeg", ".jpg", ".gif", ".webp", ".pdf"];
            function isAllowedFileType(file: File): boolean {
              if (allowedMimeTypes.includes(file.type)) {
                return true;
              }
              const lower = file.name.toLowerCase();
              return allowedExtensions.some((ext) => lower.endsWith(ext));
            }
            for (const file of files) {
              if (!isAllowedFileType(file)) {
                presentAlert(
                  L10n.localize((s) => s.funcs.general.fileTypeNotAllowed)(file.name, allowedExts),
                );
                continue;
              }
              if (file.size > MAX_FILE_SIZE_BYTES) {
                presentAlert(
                  L10n.localize((s) => s.funcs.general.fileTooLarge)(
                    file.name,
                    (file.size / 1024 / 1024).toFixed(2),
                    MAX_FILE_SIZE_MB,
                  ),
                );
                continue;
              }
              validFiles.push(file);
            }
            setFiles((prev) => prev.concat(validFiles));
          }}
        />
        <FileDropZone
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100%",
          }}
          onFilesSelected={handleFilesSelected}
          disabled={disabled || !aiFlag.with_image}
        >
          {gens.length < 1 ? (
            <>
              <FieldsCard fields={nonRefFields} />
              <TipDot
                dotID="generate_cards_modal_suggestions"
                message={L10n.localize((s) => s.generation.suggestedUses)}
              />
            </>
          ) : undefined}

          {gens.length < 1 && !generating ? (
            <div
              style={{
                marginTop: "auto",
                padding: "16px",
                width: "100%",
                maxWidth: "400px",
                margin: "auto",
              }}
            >
              {loadingSuggestedPrompts ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "32px",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <IonSpinner />
                  <IonLabel style={{ color: "var(--ion-color-medium)", fontSize: "14px" }}>
                    Getting suggestions...
                  </IonLabel>
                </div>
              ) : suggestedPrompts.length > 0 ? (
                <>
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <LuLightbulb size={18} color="var(--ion-color-primary)" />
                    <IonLabel
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "var(--ion-color-primary)",
                      }}
                    >
                      {L10n.localize((s) => s.general.suggestedPrompts)}
                    </IonLabel>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "8px",
                    }}
                  >
                    {suggestedPrompts.map((promptText, index) => {
                      const { icon, background } = getPromptIcon(promptText);
                      return (
                        <div
                          key={`${promptText}-${index}`}
                          role="button"
                          tabIndex={0}
                          aria-label={`Use suggested prompt: ${promptText}`}
                          onClick={() => {
                            setPrompt(promptText);
                            generate();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setPrompt(promptText);
                              generate();
                            }
                          }}
                          style={{
                            background: "var(--ion-color-light)",
                            border: "1px solid var(--ion-color-light-shade)",
                            borderRadius: "12px",
                            padding: "14px 16px",
                            cursor: "pointer",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            minHeight: "56px",
                            WebkitTapHighlightColor: "transparent",
                            outline: "none",
                            position: "relative",
                            overflow: "hidden",
                          }}
                          onMouseEnter={(e) => {
                            if (window.matchMedia("(hover: hover)").matches) {
                              e.currentTarget.style.transform = "translateY(-1px)";
                              e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                              e.currentTarget.style.borderColor = "var(--ion-color-primary)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (window.matchMedia("(hover: hover)").matches) {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                              e.currentTarget.style.borderColor = "var(--ion-color-light-shade)";
                            }
                          }}
                          onTouchStart={(e) => {
                            e.currentTarget.style.transform = "scale(0.98)";
                            e.currentTarget.style.opacity = "0.8";
                          }}
                          onTouchEnd={(e) => {
                            setTimeout(() => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.opacity = "1";
                            }, 100);
                          }}
                        >
                          <div
                            style={{
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "32px",
                              height: "32px",
                              background: background,
                              borderRadius: "8px",
                            }}
                          >
                            {icon}
                          </div>
                          <IonLabel
                            style={{
                              flex: 1,
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "var(--ion-color-dark)",
                              lineHeight: "1.4",
                              textAlign: "left",
                              wordBreak: "break-word",
                            }}
                          >
                            {promptText}
                          </IonLabel>
                          <div
                            style={{
                              flexShrink: 0,
                              opacity: 0.6,
                              transition: "opacity 0.2s ease",
                            }}
                          >
                            <LuWand2 size={14} color="var(--ion-color-medium)" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 16px",
                    color: "var(--ion-color-medium)",
                  }}
                >
                  <LuLightbulb size={24} color="var(--ion-color-medium)" style={{ opacity: 0.6 }} />
                  <IonLabel
                    style={{
                      display: "block",
                      marginTop: "12px",
                      fontSize: "14px",
                      fontStyle: "italic",
                    }}
                  >
                    No suggestions available right now
                  </IonLabel>
                </div>
              )}
            </div>
          ) : undefined}

          {gens.map((gen, gi) => {
            return (
              <Fragment key={gi}>
                {(gen.files && gen.files.length > 0) || gen.prompt ? (
                  <PromptCard files={gen.files || []} prompt={gen.prompt} />
                ) : undefined}
                <GenResults
                  deck={deck}
                  genIndex={gi}
                  rows={gen.rows}
                  columns={nonRefColumns}
                  updateRow={updateRow}
                />
              </Fragment>
            );
          })}

          {(files?.length && files.length > 0) || (generating && prompt) ? (
            <PromptCard
              files={files}
              prompt={prompt}
              clearAttachment={(idx?: number) => {
                if (idx === undefined) {
                  setPrompt(undefined);
                  setFiles([]);
                } else {
                  setFiles((prev) => prev?.filter((_, i) => i !== idx));
                }
              }}
            />
          ) : undefined}

          {generating ? (
            <IonCard style={{ width: "calc(100% - 24px)", padding: 8 }}>
              <LoadingTable numRows={5} numCols={2} />
            </IonCard>
          ) : undefined}

          {gens.length > 0 && !generating ? (
            <IonNote>
              <div
                className="ion-margin-horizontal ion-text-wrap"
                style={{ textAlign: "center", marginTop: 0 }}
              >
                {numDupes && numDupes > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      marginBottom: 4,
                    }}
                  >
                    <LuAlertTriangle />
                    {`${numDupes} ${L10n.localize((s) => s.card.numDuplicatesDetected)}`}
                  </div>
                ) : undefined}
                <div>{`${numEnabled}/${numTotal} ${L10n.localize(
                  (s) => s.general.selected,
                ).toLowerCase()}`}</div>
              </div>
            </IonNote>
          ) : undefined}

          <IonNote style={{ marginTop: "auto" }}>
            {!generated && !generating ? null : (
              <p
                className="ion-margin-horizontal ion-text-wrap"
                style={{
                  textAlign: "center",
                  marginLeft: "auto",
                  marginRight: "auto",
                  maxWidth: 640,
                }}
              >
                {L10n.localize((s) => s.card.generateFallibilityWarning)}
              </p>
            )}
          </IonNote>
        </FileDropZone>
      </IonContent>
    </IonPage>
  );
}
